"use client";

// CHAT-AUTOSCROLL: one hook for every message list (channel, DM, thread, task drawer). While the
// reader is at or near the bottom (NEAR_BOTTOM_PX) the view stays pinned to the newest message:
// a new message, a message growing (streaming text), an image loading late, the viewport itself
// resizing (a typing or status line appearing under the list). Opening a conversation lands on
// the newest message and sending always scrolls. A reader who has scrolled up is left alone;
// `unseen` counts what arrived meanwhile (the "New messages" pill) and `jumpToBottom` goes there.
import * as React from "react";

/** Within this many pixels of the bottom counts as "at the bottom". */
export const NEAR_BOTTOM_PX = 120;

/**
 * Dispatched on `window` by the app's router after each render. The router's scroll
 * restoration (by element position in the page, not by conversation) may then put a viewport
 * back where another conversation's was; a list that was pinned goes back to the newest message.
 */
export const ROUTE_RENDERED_EVENT = "companyos:route-rendered";

export type ScrollMetrics = { scrollHeight: number; scrollTop: number; clientHeight: number };

export function isNearBottom(m: ScrollMetrics, threshold = NEAR_BOTTOM_PX): boolean {
  return m.scrollHeight - m.scrollTop - m.clientHeight <= threshold;
}

/** What changed at the end of the list between two renders. */
export type TailChange =
  | { kind: "same" }
  /** A different conversation (the old newest message is gone), or the first one shown. */
  | { kind: "opened" }
  /** `added` messages came in after the old newest one; `mine`: the newest is the reader's. */
  | { kind: "appended"; added: number; mine: boolean };

/**
 * Compares the list's ids with the ones before. Earlier messages loading above change nothing
 * at the tail ("same"); a newest id that was not there before, with the old newest still in the
 * list, is an arrival; with the old newest gone it is another conversation.
 */
export function classifyTail(
  prevIds: readonly string[] | null,
  ids: readonly string[],
  mineIds: ReadonlySet<string> = new Set(),
): TailChange {
  if (prevIds === null) return ids.length ? { kind: "opened" } : { kind: "same" };
  const last = ids[ids.length - 1];
  const prevLast = prevIds[prevIds.length - 1];
  if (last === prevLast) return { kind: "same" };
  if (prevLast === undefined) return last === undefined ? { kind: "same" } : { kind: "opened" };
  const at = ids.lastIndexOf(prevLast);
  if (at < 0) return last === undefined ? { kind: "same" } : { kind: "opened" };
  const added = ids.length - 1 - at;
  if (added <= 0) return { kind: "same" };
  return { kind: "appended", added, mine: last !== undefined && mineIds.has(last) };
}

/**
 * Whether the reader is still "at the bottom" after a scroll event. Reaching the bottom pins;
 * only moving UP unpins. A scroll event that arrives after our own scroll to the end, by which
 * time the content has grown again (an image sized in), moves down or not at all: that is not
 * the reader leaving.
 */
export function nextPinned(pinned: boolean, near: boolean, top: number, lastTop: number): boolean {
  if (near) return true;
  return top < lastTop - 1 ? false : pinned;
}

export type FollowAction = { scroll: boolean; unseen: number };

/** What to do about a tail change: scroll down, or (reader is up in history) count it unseen. */
export function followAction(change: TailChange, pinned: boolean, unseen: number): FollowAction {
  if (change.kind === "same") return { scroll: false, unseen };
  if (change.kind === "opened") return { scroll: true, unseen: 0 };
  if (change.mine || pinned) return { scroll: true, unseen: 0 };
  return { scroll: false, unseen: unseen + change.added };
}

export type StickToBottomOptions = {
  /** The scrolling element. */
  viewport: React.RefObject<HTMLElement | null>;
  /** Ids of the listed messages, oldest first. */
  ids: readonly string[];
  /** Ids written by the reader (a newly arrived one of these always scrolls). */
  mineIds?: ReadonlySet<string>;
  threshold?: number;
};

export function useStickToBottom(o: StickToBottomOptions) {
  const { viewport, ids } = o;
  const threshold = o.threshold ?? NEAR_BOTTOM_PX;
  const pinned = React.useRef(true);
  const lastTop = React.useRef(0);
  const prevIds = React.useRef<readonly string[] | null>(null);
  const [unseen, setUnseen] = React.useState(0);
  const mineRef = React.useRef(o.mineIds);
  mineRef.current = o.mineIds;

  const toBottom = React.useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    lastTop.current = el.scrollTop;
    pinned.current = true;
  }, [viewport]);

  // The tail changed: follow it, or count it for the pill.
  React.useLayoutEffect(() => {
    const change = classifyTail(prevIds.current, ids, mineRef.current);
    prevIds.current = ids;
    if (change.kind === "same") return;
    const wasPinned = pinned.current;
    if (followAction(change, wasPinned, 0).scroll) toBottom();
    setUnseen((u) => followAction(change, wasPinned, u).unseen);
  }, [ids, toBottom]);

  // Track whether the reader is at the bottom; reaching it clears the pill.
  React.useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const onScroll = () => {
      const near = isNearBottom(el, threshold);
      pinned.current = nextPinned(pinned.current, near, el.scrollTop, lastTop.current);
      lastTop.current = el.scrollTop;
      if (near) setUnseen(0);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [viewport, threshold]);

  // Late growth keeps a pinned view on the newest message: content resizing (streaming text,
  // images sizing in), the viewport resizing, and image `load` (no layout box change before it).
  React.useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const follow = () => {
      if (!pinned.current) return;
      el.scrollTop = el.scrollHeight;
      lastTop.current = el.scrollTop;
    };
    const onLoad = (e: Event) => {
      if (e.target instanceof HTMLImageElement) follow();
    };
    el.addEventListener("load", onLoad, true);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(follow);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener("load", onLoad, true);
      ro?.disconnect();
    };
  }, [viewport]);

  // After a route renders, a pinned list is put back at the end (see ROUTE_RENDERED_EVENT). The
  // pin is read now: the restoration's scroll event, next frame, would otherwise unpin it.
  React.useEffect(() => {
    let frame = 0;
    const onRendered = () => {
      if (!pinned.current) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        pinned.current = true;
        toBottom();
      });
    };
    window.addEventListener(ROUTE_RENDERED_EVENT, onRendered);
    return () => {
      window.removeEventListener(ROUTE_RENDERED_EVENT, onRendered);
      cancelAnimationFrame(frame);
    };
  }, [toBottom]);

  const jumpToBottom = React.useCallback(() => {
    toBottom();
    setUnseen(0);
  }, [toBottom]);

  return { unseen, jumpToBottom };
}
